import './UsersTable.scss';
import { AsyncStateNotification } from '@ui/notifications';
import { InviteModal } from './InviteModal';
import { NOTIFICATIONS_COPY } from '@sonar-software/sonar-ts-constants';
import { PaginatedTable } from '@ui/tables';
import { securityActions } from '@sonar-software/sonar-security';
import { coreActions, FeatureFlag } from '@sonar-software/sonar-core';
import { createAsyncSelectors, createSonarSelector } from '@lib/selectors';
import { getUserNameColumn, getUserOwnerCountColumn, getUserReadOnlyColumn, getUserStakeholderCountColumn, getUserStatusColumn, userEmailColumn } from './UsersTableColumns';
import { keyPaginationDefaultQueryParams, keyPaginationFallbackQueryParams } from '@ui/KeyPagination';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAction, useAsyncState, useCurrentUser, useFeatureFlag, useQueryParams, useSonarSelector } from '@hooks';
const usersSelectors = createAsyncSelectors((state) => state.core.customers.byId.users.get);
const inviteLinkSelectors = createAsyncSelectors((state) => state.security.invitations.link.get);
const linkSelector = createSonarSelector((state) => { var _a; return (_a = inviteLinkSelectors.data(state)) === null || _a === void 0 ? void 0 : _a.link; });
const getRowKey = (user) => user.id;
export const UsersTable = () => {
    const [inviteUser, setInviteUser] = useState(null);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const { isReadOnly, id: userId } = useCurrentUser();
    const hasDataDictionary = useFeatureFlag(FeatureFlag.DataDictionary);
    const { data: users, isRequested: isLoadingUsers } = useAsyncState(usersSelectors);
    const userInviteLinkRequesting = useSonarSelector(inviteLinkSelectors.isRequested);
    const userInviteLink = useSonarSelector(linkSelector);
    const requestUsers = useAction(coreActions.requestGetCoreCustomersByIdUsers);
    const requestPostUser = useAction(coreActions.requestPostCoreUsersById);
    const requestInviteLink = useAction(securityActions.requestGetSecurityInvitationsLink);
    const resetRequestInviteLink = useAction(securityActions.resetGetSecurityInvitationsLink);
    const { queryParams, updateParams } = useQueryParams({
        defaultParams: keyPaginationDefaultQueryParams,
        fallbackParams: keyPaginationFallbackQueryParams
    });
    useEffect(() => {
        requestUsers({
            count: queryParams.count,
            key: queryParams.key,
            paginationDirection: queryParams.paginationDirection
        });
    }, [queryParams.count, queryParams.paginationDirection, queryParams.key, requestUsers]);
    const onAfterCloseInviteModal = useCallback(() => {
        resetRequestInviteLink();
        setInviteUser(null);
    }, [resetRequestInviteLink]);
    const handleOnCancelInviteModal = useCallback(() => {
        setShowInviteModal(false);
    }, []);
    const readOnlyClickHandler = useCallback((user) => {
        requestPostUser({
            isReadOnly: !user.isReadOnly,
            userId: user.id
        });
    }, [requestPostUser]);
    const inviteClickHandler = useCallback((user) => {
        requestInviteLink({
            email: user.email
        });
        setInviteUser(user);
        setShowInviteModal(true);
    }, [requestInviteLink]);
    const lockToggleHandler = useCallback((id, isLocked) => {
        requestPostUser({
            isLocked,
            userId: id
        });
    }, [requestPostUser]);
    const columns = useMemo(() => [
        getUserNameColumn(userId),
        userEmailColumn,
        getUserReadOnlyColumn(userId, isReadOnly, readOnlyClickHandler),
        getUserOwnerCountColumn(!hasDataDictionary),
        getUserStakeholderCountColumn(!hasDataDictionary),
        getUserStatusColumn(userId, isReadOnly || userInviteLinkRequesting, inviteClickHandler, lockToggleHandler)
    ], [
        hasDataDictionary,
        inviteClickHandler,
        isReadOnly,
        lockToggleHandler,
        readOnlyClickHandler,
        userId,
        userInviteLinkRequesting
    ]);
    return (React.createElement(React.Fragment, null,
        React.createElement(PaginatedTable, { columns: columns, headerSize: 'small', id: 'users-table', loading: isLoadingUsers, onChange: updateParams, resource: users, rowKey: getRowKey, scroll: { x: 'max-content' }, sticky: true }),
        userInviteLink && inviteUser &&
            React.createElement(InviteModal, { afterClose: onAfterCloseInviteModal, inviteLink: userInviteLink, onCancel: handleOnCancelInviteModal, open: showInviteModal, user: inviteUser }),
        React.createElement(AsyncStateNotification, { errored: NOTIFICATIONS_COPY.ERROR_GET_USER_INVITE_LINK, failed: NOTIFICATIONS_COPY.FAILED_GET_USER_INVITE_LINK, stateSelector: inviteLinkSelectors.state })));
};
